import React, { useState, useEffect } from 'react';
import { School, X } from 'lucide-react';

// Driver-portal banner for DoE cover changes the office has made (DoE & Depots → QR Codes →
// Assign a driver). Three kinds, all from doe-assign-sb getMyDoECover:
//   covering      — you are doing a school's collections for these dates
//   coveredForMe  — someone else is doing YOUR school on these dates, so don't go
//   permanent     — a school became yours (or stopped being yours) in the last 14 days
// Each can be dismissed with "Got it"; the dismissal is keyed to the exact dates, so
// extending or changing a cover shows the banner again.
const API = '/.netlify/functions/doe-assign-sb';
const SEEN_KEY = 'hp_doe_cover_seen';

const shortDay = (iso) => new Date(iso + 'T12:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
const readSeen = () => { try { return JSON.parse(localStorage.getItem(SEEN_KEY) || '[]'); } catch { return []; } };

export default function DoECoverCard({ shift }) {
  const driver = (shift?.driver || '').toUpperCase();
  const [items, setItems] = useState([]);
  const [seen, setSeen] = useState(readSeen);

  useEffect(() => {
    if (!driver) return;
    fetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'getMyDoECover' }) })
      .then(r => r.json())
      .then(d => {
        if (!d || !d.success) return;
        const out = [];
        (d.covering || []).forEach(c => out.push({
          id: `c|${c.school}|${c.dates.join(',')}`, tone: 'gold',
          title: `You're collecting ${c.school}`,
          body: <>{c.dates.length === 1 ? `On ${shortDay(c.from)}` : `${c.dates.length} collections: ${c.dates.map(shortDay).join(', ')}`}{c.original ? `, covering for ${c.original}` : ''}. Scan the QR on site and confirm it in Tasks.</>,
        }));
        (d.coveredForMe || []).forEach(c => out.push({
          id: `o|${c.school}|${c.relief}|${c.dates.join(',')}`, tone: 'dim',
          title: `${c.relief} is covering ${c.school}`,
          body: <>You don't need to collect it on {c.dates.map(shortDay).join(', ')}.</>,
        }));
        (d.permanent || []).forEach(n => out.push(n.login === driver ? {
          id: `p|${n.school}|${n.set_at}`, tone: 'gold',
          title: `${n.school} is now your DoE collection`,
          body: <>From {shortDay(n.from)}, every collection day in school term. It shows in Tasks on the day.</>,
        } : {
          id: `p|${n.school}|${n.set_at}`, tone: 'dim',
          title: `${n.school} has moved to ${n.login}`,
          body: <>From {shortDay(n.from)} you no longer collect it.</>,
        }));
        setItems(out);
      })
      .catch(() => { /* the portal must not break on this */ });
  }, [driver]);

  const visible = items.filter(i => !seen.includes(i.id));
  if (!visible.length) return null;

  function dismiss(id) {
    const next = [...seen, id].slice(-50);
    setSeen(next);
    try { localStorage.setItem(SEEN_KEY, JSON.stringify(next)); } catch { /* private mode */ }
  }

  return (
    <div style={{ width: '100%', marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {visible.map(i => (
        <div key={i.id} style={{
          padding: '14px 16px', borderRadius: 12, display: 'flex', gap: 12, alignItems: 'flex-start',
          background: i.tone === 'gold' ? 'rgba(212,175,55,0.10)' : 'var(--hp-surface)',
          border: `1px solid ${i.tone === 'gold' ? 'var(--hp-gold)' : 'var(--hp-border)'}`,
        }}>
          <School size={22} color={i.tone === 'gold' ? 'var(--hp-gold)' : 'var(--hp-dim)'} style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{i.title}</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', marginTop: 2, lineHeight: 1.4 }}>{i.body}</div>
          </div>
          <button onClick={() => dismiss(i.id)} aria-label="Got it"
            style={{ background: 'none', border: 'none', color: 'var(--hp-dim)', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.75rem', fontWeight: 600 }}>
            Got it <X size={14} />
          </button>
        </div>
      ))}
    </div>
  );
}
